import { Button, Form, Input, Modal, Select, Space } from 'antd';
import React, { FC, useEffect } from 'react';
import { ReactComponent as SaveOutlined } from 'assets/icons/save.svg';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { IModal } from '../../../../../../common/models/model.interface';
import { notification } from '../../../../../../common/utils/notification';
import { visitorBanEntryTypeOptions } from 'common/constans/visitor-ban-entry.constants';
import { VisitorBanEntryType } from 'common/enums/visitor-ban-entry-type.enum';
import visitorBanListService from 'common/services/visitor-ban-list-service/visitor-ban-list.service';
import styles from './../default/default-configuration.module.scss';
import TextArea from 'antd/es/input/TextArea';

export interface IVisitorBanEntryAddEditModal {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  licensePlate: string | null;
  type: string;
  action: string | null;
  reason: string | null;
}

const VisitorBanEntryAddEditModal: FC<IModal<IVisitorBanEntryAddEditModal, any>> = ({ isOpen, onClose, isEdit, title, initData }) => {
  const [form] = Form.useForm();
  const visitorBanEnatryType = Form.useWatch(['type'], form);
  const selectedAction = Form.useWatch(['action'], form);

  const onFinish = async (values: any) => {
    const { customAction, ...rest } = values;

    const payload = {
      ...rest,
      action: values.action === 'Other' ? customAction.trim() : values.action,
    };

    const result =
      isEdit && initData ? await visitorBanListService.update(initData.id!, payload) : await visitorBanListService.create(payload);

    hideModal(true, result);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    form.resetFields();

    if (!initData) {
      return;
    }

    const action = initData.action;
    const isCustomAction = !!action && !actionOptions.some((option) => option.value === action);

    form.setFieldsValue({
      ...initData,
      action: isCustomAction ? 'Other' : action,
      customAction: isCustomAction ? action : undefined,
    });
  }, [isOpen, form, initData]);

  const hideModal = (...rest: any) => {
    notification.destroy();
    form.resetFields();
    if (onClose) {
      onClose(...rest);
    }
  };

  return (
    <Modal centered title={title} width={550} open={isOpen} wrapClassName="custom-modal" closable={false}>
      <FormWrapper form={form} onFinish={onFinish} initialValues={initData} layout="vertical">
        <Space className="full-width" size={13}>
          <Form.Item name="type" label="Type" rules={visitorBanEntryValidator.type}>
            <Select placeholder="Select type" options={visitorBanEntryTypeOptions} disabled={isEdit} />
          </Form.Item>
        </Space>
        {visitorBanEnatryType === VisitorBanEntryType.Person && (
          <>
            <Space className="full-width" size={13}>
              <Form.Item name="firstName" label="First Name" rules={visitorBanEntryValidator.firstName}>
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Space>
            <Space className="full-width" size={13}>
              <Form.Item name="lastName" label="Last Name" rules={visitorBanEntryValidator.lastName}>
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Space>
          </>
        )}
        {(visitorBanEnatryType === VisitorBanEntryType.Person || visitorBanEnatryType === VisitorBanEntryType.Company) && (
          <Space className="full-width" size={13}>
            <Form.Item
              name="companyName"
              label="Company Name"
              rules={
                visitorBanEnatryType === VisitorBanEntryType.Person
                  ? visitorBanEntryValidator.companyName
                  : visitorBanEntryValidator.companyNameRequired
              }>
              <Input placeholder="Enter company name" />
            </Form.Item>
          </Space>
        )}
        {(visitorBanEnatryType === VisitorBanEntryType.Person || visitorBanEnatryType === VisitorBanEntryType.Vehicle) && (
          <Space className="full-width" size={13}>
            <Form.Item
              name="licensePlate"
              label="License Plate"
              rules={
                visitorBanEnatryType === VisitorBanEntryType.Person
                  ? visitorBanEntryValidator.licensePlate
                  : visitorBanEntryValidator.licensePlateRequired
              }>
              <Input placeholder="Enter license plate" />
            </Form.Item>
          </Space>
        )}
        <Space className="full-width" size={13}>
          <Form.Item name="action" label="Action" rules={visitorBanEntryValidator.action}>
            <Select options={actionOptions} placeholder="Select action" />
          </Form.Item>
        </Space>
        {selectedAction === 'Other' && (
          <Space className="full-width" size={13}>
            <Form.Item name="customAction" label="Other action" preserve={false} rules={visitorBanEntryValidator.customAction}>
              <TextArea showCount maxLength={127} className={styles.eiqTextArea} placeholder="Enter action" />
            </Form.Item>
          </Space>
        )}
        <Space className="full-width" size={13}>
          <Form.Item name="reason" label="Reason" rules={visitorBanEntryValidator.reason}>
            <TextArea showCount maxLength={1000} className={styles.eiqTextArea} placeholder="Enter reason" />
          </Form.Item>
        </Space>

        <Space className="footer">
          <Button type="link" onClick={() => hideModal()} style={{ width: '159px' }}>
            Cancel
          </Button>

          <Button htmlType="submit" type="primary" style={{ width: '159px' }} icon={<SaveOutlined />}>
            Save
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
};

export default React.memo(VisitorBanEntryAddEditModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});

const actionOptions = [
  { label: 'Call the Police', value: 'Call the Police' },
  { label: 'Turn Back', value: 'Turn Back' },
  { label: 'Restraining Order', value: 'Restraining Order' },
  { label: 'Other', value: 'Other' },
];

const visitorBanEntryValidator = {
  customAction: [
    {
      required: true,
      whitespace: true,
      message: 'Please enter an action!',
    },
    {
      max: 127,
      message: 'The max action length is 127 characters!',
    },
  ],
  type: [{ required: true, message: 'Type is required!' }],
  firstName: [
    { required: true, message: 'First Name is required!' },
    { max: 255, message: 'The max first name length is 255 characters!' },
  ],
  lastName: [
    { required: true, message: 'Last Name is required!' },
    { max: 255, message: 'The max last name length is 255 characters!' },
  ],
  companyName: [{ max: 255, message: 'The max company name length is 255 characters!' }],
  companyNameRequired: [
    { required: true, message: 'Company Name is required!' },
    { max: 255, message: 'The max company name length is 255 characters!' },
  ],
  licensePlate: [{ max: 127, message: 'The max license plate length is 127 characters!' }],
  licensePlateRequired: [
    { required: true, message: 'License Plate is required!' },
    { max: 127, message: 'The max license plate length is 127 characters!' },
  ],
  action: [
    { required: true, message: 'Action is required!' },
    { max: 127, message: 'The max action length is 127 characters!' },
  ],
  reason: [
    { required: true, message: 'Reason is required!' },
    { max: 127, message: 'The max reason length is 1000 characters!' },
  ],
};
